import type { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import type { OrganizationRole } from '../../types/organization';

interface RequireRoleProps {
  /** Minimum role required to render children */
  role: OrganizationRole;
  /** Content to render when user has sufficient role */
  children: ReactNode;
  /** Optional fallback content when user doesn't have sufficient role */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on user's role in current organization.
 *
 * @example
 * <RequireRole role="admin">
 *   <AdminOnlyButton />
 * </RequireRole>
 *
 * @example
 * <RequireRole role="owner" fallback={<span>Owner only</span>}>
 *   <DeleteOrgButton />
 * </RequireRole>
 */
export function RequireRole({ role, children, fallback = null }: RequireRoleProps) {
  const { roleAtLeast } = usePermissions();

  if (!roleAtLeast(role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface IfRoleProps {
  /** Exact role to check for */
  role: OrganizationRole;
  /** Content to render when user has the exact role */
  children: ReactNode;
}

/**
 * Renders children only if user has the exact specified role.
 *
 * @example
 * <IfRole role="owner">
 *   <OwnerBadge />
 * </IfRole>
 */
export function IfRole({ role, children }: IfRoleProps) {
  const { isOwner, isAdmin, isMember, isViewer } = usePermissions();

  const hasRole =
    (role === 'owner' && isOwner) ||
    (role === 'admin' && isAdmin && !isOwner) ||
    (role === 'member' && isMember && !isAdmin) ||
    (role === 'viewer' && isViewer && !isMember);

  if (!hasRole) {
    return null;
  }

  return <>{children}</>;
}

interface CanManageMembersProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Renders children only if user can manage members (admin+)
 */
export function CanManageMembers({ children, fallback = null }: CanManageMembersProps) {
  const { canManageMembers } = usePermissions();
  return canManageMembers ? <>{children}</> : <>{fallback}</>;
}

interface CanDeleteOrgProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Renders children only if user can delete the organization (owner only)
 */
export function CanDeleteOrg({ children, fallback = null }: CanDeleteOrgProps) {
  const { canDeleteOrg } = usePermissions();
  return canDeleteOrg ? <>{children}</> : <>{fallback}</>;
}
